import { ApiUrlInput } from '@neo/blocks/api-url-input'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { Label } from '@neo/primitives/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import type { AxiosError } from 'axios'
import cloneDeep from 'lodash/cloneDeep'
import { ArrowLeftRight } from 'lucide-react'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { flowService } from 'v2/shared/services/flow-service'
import { NodevarList } from '../../components/nodevar-list'
import { v2validations } from '../../config/validation.config'
import { useFlowStore } from '../../store'
import type { HttpRequestNodeData, SidebarComponentProps } from '../../types'
import { hasDependentFieldsChanged } from '../../utils/http-request-utils'
import { GenericSidebarComponent } from '../sidebar.base'
import { AuthTab } from './auth-tab'
import { BodyTab } from './body-tab'
import { CurlImportDialog } from './curl-import-dialog'
import { HeaderTab } from './header-tab'
import { ParamsTab } from './params-tab'

export type FormValues = HttpRequestNodeData['config']
type AuthParamsType =
  | {
      username?: string | undefined
      password?: string | undefined
      token?: string | undefined
    }
  | string

const handleFetchResponse = async (form: GenericFormType<HttpRequestNodeData['config']>) => {
  try {
    const payload = cloneDeep(form.state.values)
    if (payload.http_request.auth_params && typeof payload.http_request.auth_params === 'object') {
      ;(payload.http_request.auth_params as AuthParamsType) = JSON.stringify(
        payload.http_request.auth_params,
      )
    }
    const jsonBody =
      typeof payload.http_request.body === 'string'
        ? JSON.parse(payload.http_request.body)[payload.http_request.body_type]
        : {}
    const jsonBodyStringified = JSON.stringify(jsonBody, null, 2)
    payload.http_request.body = typeof jsonBody === 'string' ? jsonBody : jsonBodyStringified

    const resp = await flowService.execHttpRequest(payload.http_request)
    const responseFormat = JSON.stringify(resp.data.response_format, null, 2)
    const responseType = resp.data.response_type.split(';')[0]

    form.setFieldValue('http_request.response_format', responseFormat)
    form.setFieldValue('http_request.response_type', responseType)
    return { success: true, responseFormat, responseType }
  } catch (error: unknown) {
    const errorMessage = error as AxiosError
    const errorObject = errorMessage.response?.data || errorMessage.response || errorMessage
    try {
      const errorMessageStringified = JSON.stringify(
        typeof errorObject === 'string' ? { error: errorObject } : errorObject,
        null,
        2,
      )
      form.setFieldValue('http_request.response_format', errorMessageStringified)
    } catch (_e: unknown) {
      const fallbackError = JSON.stringify({ error: 'Unknown error occurred' }, null, 2)
      form.setFieldValue('http_request.response_format', fallbackError)
    }
    return {
      success: false,
      error: errorMessage,
      responseFormat: undefined,
      responseType: undefined,
    }
  }
}

const fields = (
  nodeVars: string[],
  handleFetchResponse?: (form: GenericFormType<HttpRequestNodeData['config']>) => Promise<{
    success: boolean
    error?: AxiosError
    responseFormat?: string
    responseType?: string
  }>,
): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).http_request
  const httpRequestSchema = validations.shape.http_request._def.schema // Get the inner schema before superRefine
  return [
    {
      name: 'name',
      componentType: 'input',
      label: 'Name',
      zodValidation: validations.shape.name,
      inputProps: {
        placeholder: 'Enter name',
      },
    },
    {
      name: 'http_request.url',
      label: 'API URL',
      zodValidation: httpRequestSchema.shape.url,
      type: 'custom',
      component: ({ form }) => (
        <ApiUrlInput
          methods={['GET', 'POST', 'PATCH', 'PUT', 'DELETE']}
          defaultMethod={form.state.values.http_request.method || 'GET'}
          placeholder="Enter a URL to make HTTP request"
          url={form.state.values.http_request.url || ''}
          onMethodChange={(method) => {
            form.setFieldValue(
              'http_request.method',
              method.toLowerCase() as 'get' | 'post' | 'put' | 'delete' | 'patch',
            )
          }}
          onUrlChange={(url) => {
            form.setFieldValue('http_request.url', url)
            form.validateField('http_request.url', 'change')
          }}
          id="http-request-url"
          endIcon={
            <NodevarList
              nodevars={nodeVars}
              className="pr-2"
              inputElementSelector="#http-request-url"
            />
          }
          label="API URL"
        />
      ),
    },
    {
      name: 'tabs',
      type: 'basic',
      component: ({ form }) => (
        <div className="flex flex-col gap-1 col-span-2">
          <CurlImportDialog form={form} />
          <Tabs defaultValue="auth" className="col-span-2">
            <TabsList variant="default" className="mb-4 flex font-inter">
              <TabsTrigger value="auth" variant="default" className="flex-1 justify-center">
                Auth
              </TabsTrigger>
              <TabsTrigger value="params" variant="default" className="flex-1 justify-center">
                Params
              </TabsTrigger>
              <TabsTrigger value="header" variant="default" className="flex-1 justify-center">
                Header
              </TabsTrigger>
              <TabsTrigger value="body" variant="default" className="flex-1 justify-center">
                Body
              </TabsTrigger>
            </TabsList>
            <TabsContent value="auth">
              <AuthTab form={form} nodeVars={nodeVars} />
            </TabsContent>
            <TabsContent value="params">
              <ParamsTab form={form} nodeVars={nodeVars} />
            </TabsContent>
            <TabsContent value="header">
              <HeaderTab form={form} nodeVars={nodeVars} />
            </TabsContent>
            <TabsContent value="body">
              <BodyTab form={form} nodeVars={nodeVars} />
            </TabsContent>
          </Tabs>
        </div>
      ),
    },
    {
      name: 'http_request.response_format',
      label: 'Response Format',
      type: 'custom',
      zodValidation: httpRequestSchema.shape.response_format,
      component: ({ form }) => {
        const responseFormat = form.state.values.http_request.response_format
        const isAPITested = responseFormat && responseFormat !== '{}'

        return (
          <div className="flex flex-col gap-4 col-span-2">
            <div className="flex justify-between flex-col gap-4">
              <Label htmlFor="response_format" className="text-h4-500 text-foreground">
                Test API Response
              </Label>
              <Button
                variant="outline"
                className="w-fit"
                onClick={() => handleFetchResponse?.(form)}
                type="button"
                data-testid="test-response-button"
              >
                <ArrowLeftRight size={14} className="mr-1" />
                {isAPITested ? 'Retest' : 'Test Response'}
              </Button>
            </div>

            {isAPITested ? (
              <div className="flex flex-col gap-0">
                <div className="flex flex-row flex-wrap gap-1 items-baseline">
                  <span className="font-inter text-s-500 text-secondary">Response</span>{' '}
                  <span className="font-jetbrains text-xs-400 text-secondary">
                    {`(Format: ${form.state.values.http_request.response_type})`}
                  </span>
                </div>
                <CodeEditor
                  key={`response-format-${responseFormat?.length || 0}-${responseFormat?.slice(0, 50) || ''}`}
                  value={responseFormat}
                  onChange={(value) => form.setFieldValue('http_request.response_format', value)}
                  className="overflow-auto"
                />
              </div>
            ) : null}
          </div>
        )
      },
    },
    {
      name: 'http_request.timeout_ms',
      zodValidation: httpRequestSchema.shape.timeout_ms,
      label: 'Time out requests after',
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        inputClassName: 'font-jetbrains text-s-400 tabular-nums',
        endIcon: <span className="font-jetbrains text-tertiary text-xs-400 mr-7">msecs</span>,
        min: 0,
      },
    },
    {
      name: 'http_request.retry_limit',
      zodValidation: httpRequestSchema.shape.retry_limit,
      label: 'Retry the request',
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        inputClassName: 'font-jetbrains text-s-400 tabular-nums',
        endIcon: <span className="font-jetbrains text-tertiary text-xs-400 mr-7">times</span>,
        min: 0,
      },
    },
  ]
}

export const HttpRequestSidebarComponent = (props: SidebarComponentProps<HttpRequestNodeData>) => {
  const { nodeVars, selectedNode } = props
  const sidebarFormInstance = useFlowStore((state) => state.sidebarFormInstance)

  const formFields = fields(nodeVars, handleFetchResponse)

  // Custom callback to handle automatic API testing before save
  const handleBeforeSave = async (data: HttpRequestNodeData['config']) => {
    if (hasDependentFieldsChanged(data, selectedNode.data.config)) {
      if (sidebarFormInstance) {
        const result = await handleFetchResponse(sidebarFormInstance)
        if (!result.success) {
          const errorMessage =
            result.error?.response?.data?.message ||
            result.error?.message ||
            'API test failed. Please check your configuration and try again.'
          throw new Error(errorMessage) // Throw error to prevent save
        }
        // Return the updated form values with the API response
        return sidebarFormInstance.state.values
      }
    }
    return data
  }

  return <GenericSidebarComponent fields={formFields} {...props} callback={handleBeforeSave} />
}
